import { Flame, AlertTriangle, Shield, CheckCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function FireSafetySection() {
  const protectionPoints = [
    "Reduce wildfire risk by up to 80%",
    "Create defensible space around structures",
    "Remove dead vegetation and fire fuel",
    "Improve property insurance eligibility",
    "HOA fire code compliance",
    "Protect property value and investment",
  ];

  const stats = [
    { number: "80%", label: "Risk Reduction" },
    { number: "1,000+", label: "Properties Protected" },
    { number: "24/7", label: "Emergency Response" },
  ];

  return (
    <section
      id="fire-safety"
      className="fire-safety-section py-28 lg:py-36"
      data-testid="fire-safety-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content Side */}
          <div>
            {/* Urgent Badge */}
            <div className="inline-flex items-center gap-2 bg-[#DC2626] text-white px-5 py-2.5 mb-8 fire-pulse">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm font-bold uppercase tracking-wider">
                Critical Fire Safety
              </span>
            </div>

            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
              Is Your Property
              <br />
              <span className="text-[#DC2626]">Protected?</span>
            </h2>

            <p className="text-white/80 text-lg mb-8 leading-relaxed">
              Arizona's desert climate creates unique fire hazards. Every year,
              thousands of acres burn—and unprotected properties face
              devastating consequences.{" "}
              <span className="text-[#DC2626] font-bold">
                Don't wait until it's too late.
              </span>
            </p>

            <div className="bg-white/5 border border-[#DC2626]/40 p-6 mb-10">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 bg-[#DC2626]/20 flex items-center justify-center flex-shrink-0">
                  <Flame className="w-6 h-6 text-[#DC2626]" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg mb-2">
                    The Hidden Danger in Your Yard
                  </h3>
                  <p className="text-white/70">
                    Dead brush, overgrown vegetation, and accumulated debris are
                    fuel waiting to ignite. A single spark during monsoon season
                    or dry summer months can turn your landscape into an
                    inferno—threatening your home, your family, and your
                    neighbors.
                  </p>
                </div>
              </div>
            </div>

            {/* Protection Points */}
            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {protectionPoints.map((point, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                  <span className="text-white/90 text-sm">{point}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-none px-8 py-5 uppercase tracking-[0.15em] text-sm font-bold shadow-lg shadow-red-500/30"
                data-testid="fire-safety-cta-urgent"
              >
                <a href="#contact" className="flex items-center gap-2">
                  <Flame className="w-4 h-4" />
                  Schedule Fire Assessment
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0A1628] rounded-none px-8 py-5 uppercase tracking-[0.15em] text-sm font-bold"
                data-testid="fire-safety-cta-call"
              >
                <a href="tel:4803517775" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  24/7 Emergency Line
                </a>
              </Button>
            </div>
          </div>

          {/* Image & Stats Side */}
          <div className="relative">
            <div className="relative overflow-hidden">
              <img
                src="https://images.pexels.com/photos/691953/pexels-photo-691953.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Arizona wildfire prevention and brush fire safety in Scottsdale and Paradise Valley"
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-transparent to-transparent" />
            </div>

            {/* Stats Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="grid grid-cols-3 gap-4">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="text-center bg-[#0A1628]/90 backdrop-blur-sm py-5 border-t-2 border-[#10B981]"
                  >
                    <p className="font-['Cormorant_Garamond'] text-2xl lg:text-3xl font-bold text-white">
                      {stat.number}
                    </p>
                    <p className="text-white/60 text-[10px] uppercase tracking-wider">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Shield Badge */}
            <div className="absolute -top-6 -right-6 bg-gradient-to-br from-[#10B981] to-[#059669] p-5 shadow-xl shadow-emerald-500/30">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>

        {/* Bottom Warning Banner */}
        <div className="mt-20 bg-[#DC2626]/10 border border-[#DC2626]/30 p-8 text-center">
          <p className="text-white text-lg">
            <span className="font-bold">Monsoon Season Is Here.</span> Our team
            is standing by 24/7 for emergency brush removal and storm damage
            response.
          </p>
          <p className="text-[#10B981] font-bold mt-3 text-lg">
            Call now for priority scheduling: (480) 351-7775
          </p>
        </div>
      </div>
    </section>
  );
}
